import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Store, Globe, ShoppingCart, ExternalLink, ArrowLeft } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StorefrontPage() {
  const { storeSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const { addItem } = useCart();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    loadStore();
  }, [storeSlug]);

  const loadStore = async () => {
    try {
      const res = await fetch(`${API_URL}/api/merchant/store/${storeSlug}`);
      const data = await res.json();
      
      if (!res.ok) {
        setError(data.detail || 'Store not found');
        return;
      }
      
      setStore(data.store);
      setProducts(data.products);
    } catch (err) {
      setError('Failed to load store');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    if (product.product_type === 'affiliate') {
      window.open(product.affiliate_url, '_blank');
      return;
    }
    addItem(product);
    toast.success('Added to cart!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Store className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Store Not Found</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to SALESSPACE
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Store Banner */}
      <div 
        className="h-48 md:h-64 bg-gradient-to-r from-blue-600 to-purple-600 relative"
        style={store?.banner_url ? { backgroundImage: `url(${store.banner_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
      >
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute top-4 left-4">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-1.5 bg-black/30 backdrop-blur rounded-lg text-white text-sm hover:bg-black/50"
          >
            <ArrowLeft className="w-4 h-4" />
            SALESSPACE
          </Link>
        </div>
      </div>

      {/* Store Info */}
      <div className="max-w-7xl mx-auto px-4 -mt-16 relative z-10">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-end gap-6">
            {/* Logo */}
            <div className="w-24 h-24 md:w-32 md:h-32 bg-slate-700 rounded-xl flex items-center justify-center -mt-16 border-4 border-slate-800">
              {store?.logo_url ? (
                <img src={store.logo_url} alt={store.name} className="w-full h-full object-cover rounded-xl" />
              ) : (
                <Store className="w-12 h-12 text-slate-400" />
              )}
            </div>
            
            {/* Store Details */}
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-white">{store?.name}</h1>
              {store?.description && (
                <p className="text-slate-400 mt-2 line-clamp-2">{store.description}</p>
              )}
              {store?.website_url && (
                <a
                  href={store.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 mt-2 text-sm"
                >
                  <Globe className="w-4 h-4" />
                  {store.website_url.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>

            {/* Stats */}
            <div className="flex gap-6 text-center">
              <div>
                <p className="text-2xl font-bold text-white">{products.length}</p>
                <p className="text-sm text-slate-400">Products</p>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-white mb-6">All Products</h2>
          
          {products.length === 0 ? (
            <div className="text-center py-16 bg-slate-800/50 rounded-xl">
              <Store className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No products available yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden hover:border-slate-600 transition-colors group"
                >
                  {/* Image */}
                  <Link to={`/products/${product.id}`}>
                    <div className="aspect-square bg-slate-700 relative overflow-hidden">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                      {product.product_type === 'affiliate' && (
                        <span className="absolute top-2 right-2 bg-purple-500 text-white text-xs px-2 py-1 rounded">
                          Affiliate
                        </span>
                      )}
                    </div>
                  </Link>
                  
                  {/* Info */}
                  <div className="p-4">
                    <Link to={`/products/${product.id}`}>
                      <h3 className="font-medium text-white line-clamp-2 hover:text-blue-400">
                        {product.name}
                      </h3>
                    </Link>
                    <p className="text-lg font-bold text-white mt-2">
                      {formatPrice(product.price)}
                    </p>
                    <button
                      onClick={() => handleAddToCart(product)}
                      className={`w-full mt-3 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                        product.product_type === 'affiliate'
                          ? 'bg-purple-600 hover:bg-purple-500 text-white'
                          : 'bg-blue-600 hover:bg-blue-500 text-white'
                      }`}
                    >
                      {product.product_type === 'affiliate' ? (
                        <>
                          <ExternalLink className="w-4 h-4" />
                          View Deal
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="w-4 h-4" />
                          Add to Cart
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center py-8 border-t border-slate-800">
          <p className="text-slate-500 text-sm">
            This store is powered by{' '}
            <Link to="/" className="text-blue-400 hover:underline">
              SALESSPACE
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
